"use client";

import { useState } from "react";
import { Notice, SelectField } from "@/components/tool-kit/primitives";
import {
  defaultGroups,
  exampleGroups,
  GroupFields,
  type GroupInput,
  MIN_GROUPS,
  readGroups,
} from "@/components/tools/stats-tests/group-fields";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { KruskalWallisResults } from "@/components/tools/stats-tests/kruskal-wallis-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { kruskalWallis } from "@/lib/tool-kit/stats-tests/kruskal-wallis";

/** English strings. A translated content file overrides these through `ui`. */
export const KRUSKAL_WALLIS_TEST_CALCULATOR_STRINGS = {
  groupName: "Group {n} name",
  groupData:
    "Group {n} data — numbers separated by commas, spaces, or new lines",
  groupDefault: "Group {n}",
  dataPlaceholder: "12, 15, 14, 10",
  valuesDetected: "one: value|other: values",
  addGroup: "Add group",
  removeGroup: "Remove last group",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleNames: "Re-reading|Practice testing|Highlighting",
  exampleData:
    "12, 15, 14, 10, 13, 16|18, 21, 17, 22, 19, 16|11, 9, 13, 12, 10, 8",
  results: "Results",
  hStatistic: "H (tie-corrected)",
  df: "Degrees of freedom",
  pValue: "p-value",
  epsilonSquared: "ε² (epsilon squared)",
  totalN: "Total (N)",
  tieCorrection: "Tie correction C",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — at least one group tends to have larger values than another.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the groups differ.",
  ranksHeading: "Ranks by group",
  group: "Group",
  n: "n",
  median: "Median",
  rankSum: "Rank sum",
  meanRank: "Mean rank",
  dunnHeading: "Dunn's test (Bonferroni-adjusted)",
  dunnHint:
    "Dunn's test compares mean ranks from the joint ranking, with the tie-adjusted standard error. Each p is multiplied by the number of comparisons ({m}), capped at 1.",
  comparison: "Comparison",
  rankDifference: "Mean-rank difference",
  z: "z",
  p: "p",
  pAdjusted: "p (Bonferroni)",
  versus: "{a} vs {b}",
  smallSample:
    "A group has fewer than five values, so the chi-square approximation for H is rough; exact tables or a permutation test are more accurate.",
  howCalculated: "How this was calculated",
  apaHeading: "APA-style result",
  apaTemplate: "*H*({df}) = {h}, *p* {p}, ε² = {eps}",
  apaNote:
    "Report the median (or mean rank) of each group, then the Dunn comparisons that explain the result.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter at least one number in each of at least two groups.",
  errorTied:
    "Every value is identical, so the ranks carry no information and H cannot be computed.",
} as const;

export default function KruskalWallisTestCalculatorWidget() {
  const strings = useToolUi(KRUSKAL_WALLIS_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [groups, setGroups] = useState<GroupInput[]>(() =>
    defaultGroups(strings.groupDefault, 3)
  );
  const [alpha, setAlpha] = useState("0.05");

  const { data, names } = readGroups(groups, strings.groupDefault);
  const outcome = data.length >= MIN_GROUPS ? kruskalWallis(data) : null;

  return (
    <div className="space-y-6">
      <GroupFields
        fmt={fmt}
        groups={groups}
        idPrefix="kw"
        locale={locale}
        onChange={setGroups}
        text={strings}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="kw-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() =>
            setGroups(exampleGroups(strings.exampleNames, strings.exampleData))
          }
        />
      </div>
      {outcome?.ok ? (
        <KruskalWallisResults
          alpha={Number(alpha)}
          data={data}
          fmt={fmt}
          names={names}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>
          {outcome && !outcome.ok && outcome.reason === "all-tied"
            ? strings.errorTied
            : strings.prompt}
        </Notice>
      )}
    </div>
  );
}
